// Contexte d'authentification global. Expose la session Supabase, l'utilisateur courant,
// et les méthodes login/logout. Lit aussi le profil utilisateur applicatif (rôles).

import type { Session, User } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { supabase } from '@/lib/supabase';

interface AuthState {
  session: Session | null;
  user: User | null;
  profil: ProfilUtilisateur | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

interface ProfilUtilisateur {
  id: string;
  organizationId: string;
  email: string;
  nom: string;
  prenom: string;
  roles: string[];
  modules: string[];
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profil, setProfil] = useState<ProfilUtilisateur | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const chargerProfil = useCallback(async (authUserId: string): Promise<ProfilUtilisateur | null> => {
    // Charge le profil applicatif (utilisateurs + roles) à partir de auth_user_id.
    // En production, créer une vue ou une RPC pour optimiser cette requête.
    const { data, error } = await supabase
      .from('utilisateurs')
      .select(
        `id, organization_id, email, nom, prenom, preferences,
         utilisateur_roles ( roles ( nom ) )`,
      )
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (error || !data) {
      console.error('Erreur chargement profil :', error);
      return null;
    }

    const roles = (data.utilisateur_roles as unknown as { roles: { nom: string } | null }[] | null)
      ?.map((ur) => ur.roles?.nom)
      .filter((n): n is string => Boolean(n)) ?? [];
    const modules =
      (data.preferences as { modules?: string[] } | null)?.modules ?? [];

    return {
      id: data.id as string,
      organizationId: data.organization_id as string,
      email: data.email as string,
      nom: data.nom as string,
      prenom: data.prenom as string,
      roles,
      modules,
      isAdmin: roles.includes('admin') || roles.includes('gerant'),
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    // On débloque l'UI dès que la session est connue ; le profil applicatif
    // (rôles) se charge en arrière-plan pour ne jamais bloquer l'affichage.
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setIsLoading(false);
      if (data.session?.user) {
        void chargerProfil(data.session.user.id).then((p) => {
          if (mounted) setProfil(p);
        });
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      if (newSession?.user) {
        void chargerProfil(newSession.user.id).then((p) => {
          if (mounted) setProfil(p);
        });
      } else {
        setProfil(null);
      }
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [chargerProfil]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfil(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      profil,
      isLoading,
      signIn,
      signOut,
    }),
    [session, profil, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un <AuthProvider>');
  return ctx;
}
